import { ARTIFACTS_BY_ID } from '../artifacts/ArtifactDefs';
import type { Profile } from './Profile';

/**
 * The shard economy (revised §4.5): artifacts drop with a quality roll in
 * [0.80, 1.20] scaling their boon values. The collection keeps the best
 * banked roll; banking a duplicate/worse copy converts it to shards; shards
 * buy +5% quality steps toward the hard 1.20 cap — upgrades can never mint
 * power past what can drop.
 */

export const QUALITY_MIN = 0.8;
export const QUALITY_MAX = 1.2;
export const UPGRADE_STEP = 0.05;

const DUP_SHARD_BASE = 60;

const UPGRADE_COST_BASE = 40;

/** Round quality to 2 decimals — keeps stored/displayed values clean. */
export function roundQuality(q: number): number {
  return Math.round(q * 100) / 100;
}

/**
 * One quality roll on [floor, QUALITY_MAX]. ALWAYS consumes exactly one rng
 * draw — even when floor ≥ cap (degenerate roll returns the cap) — so a
 * tier-raised floor changes VALUES, never seeded-stream draw counts/order.
 */
export function rollQuality(rng: { next(): number }, floor: number = QUALITY_MIN): number {
  const f = Math.min(floor, QUALITY_MAX);
  return roundQuality(f + rng.next() * (QUALITY_MAX - f));
}

/** Shards received when a duplicate/worse copy is banked on a win. */
export function dupShardValue(quality: number): number {
  return Math.round(DUP_SHARD_BASE * quality);
}

/**
 * Cost of the next +5% step: cheap at 0.80, ~2.75× near the cap — capping an
 * artifact is a project, the first steps are quick wins.
 */
export function upgradeCost(currentQuality: number): number {
  const t = (currentQuality - QUALITY_MIN) / (QUALITY_MAX - QUALITY_MIN);
  return Math.round(UPGRADE_COST_BASE * (1 + 1.75 * t));
}

// --- Run-end gem tithe (July 2026) -----------------------------------------
// A slice of the run's monotonic gemsEarned converts to shards at run end —
// EVERY run, so failed runs still buy meta upgrades. Run-end ONLY, never at
// descent banking: gemsEarned spans the whole run, so converting per descent
// would re-tithe earlier floors at every checkpoint.
// Calibration (Depths rework): the bar is kill-fed now, but the tithe stays
// on gemsEarned — a strong full run lands ~3.6–5k gemsEarned (balance bot:
// ~3.6k at bar-full; KILL_GEM_NORM holds this steady under ×2 density), so
// death yields ~90–125 shards, a win ~180–250.

export const GEM_KEEP_DEATH = 0.25;
export const GEM_KEEP_WIN = 0.5;
export const GEMS_PER_SHARD = 10;

/**
 * `titheMult` is the RESOLVED tier multiplier (tiers.tierTitheMult) passed in
 * by the caller — keeps economy.ts free of a tiers import cycle, and flooring
 * happens after the multiplier so partial shards still round down.
 */
export function gemShardYield(gemsEarned: number, win: boolean, titheMult = 1): number {
  const kept = gemsEarned * (win ? GEM_KEEP_WIN : GEM_KEEP_DEATH) * titheMult;
  return Math.max(0, Math.floor(kept / GEMS_PER_SHARD));
}

export type UpgradeCheck = 'ok' | 'unowned' | 'atCap' | 'broke';

export function canUpgrade(profile: Profile, artifactId: string): UpgradeCheck {
  const q = profile.ownedQuality(artifactId);
  if (q === undefined) return 'unowned';
  if (q >= QUALITY_MAX) return 'atCap';
  if (!ARTIFACTS_BY_ID.has(artifactId)) return 'unowned';
  if (profile.data.shards < upgradeCost(q)) return 'broke';
  return 'ok';
}

/** Spend shards for +5% quality, hard-clamped at the cap. Saves on success. */
export function upgradeArtifact(profile: Profile, artifactId: string): UpgradeCheck {
  const check = canUpgrade(profile, artifactId);
  if (check !== 'ok') return check;
  const q = profile.ownedQuality(artifactId)!;
  profile.data.shards -= upgradeCost(q);
  profile.data.collection[artifactId] = roundQuality(Math.min(QUALITY_MAX, q + UPGRADE_STEP));
  profile.save();
  return 'ok';
}
